import { FC } from 'react'

import i18n from 'modules/core/lib/i18n'
import type LegalEntity from 'modules/core/store/models/LegalEntity'

import contractVersions from 'modules/contracts/store/collections/contractVersions'
import { BankInfo } from 'modules/employees/components/blocks/BankInfo'
import { ContactBlock } from 'modules/employees/components/blocks/Contact'
import { EmployeeAddressBlock } from 'modules/employees/components/blocks/EmployeeAddress'
import { Identification } from 'modules/employees/components/blocks/EmployeeIdentification'
import { FamilySituation } from 'modules/employees/components/blocks/FamilySituation'
import { GeneralBlock } from 'modules/employees/components/blocks/General'
import { PersonalSituation } from 'modules/employees/components/blocks/PersonalSituation'
import {
  TaxResidence,
  shouldShowTaxResidence,
} from 'modules/employees/components/blocks/TaxResidence'
import type Employee from 'modules/employees/store/models/Employee'
import { DataCollectionCategories } from 'modules/processes/domain'
import { EmployeeQuestionsCustomFields } from 'modules/profile/components/ProfileForm/EmployeeQuestionsCustomFields'

import Warning from '../Warning'

interface Props {
  employee: Employee
  legalEntity: LegalEntity | null
  isEligibleForPayroll: boolean
  readonly: boolean
  fieldIds?: number[]
  isSpanish: boolean
  category: DataCollectionCategories
  canSeePersonalInfo: boolean
  canEditPersonalInfo: boolean
}

const EmployeeData: FC<Props> = ({
  employee,
  legalEntity,
  isEligibleForPayroll,
  readonly,
  fieldIds,
  isSpanish,
  category,
  canSeePersonalInfo,
  canEditPersonalInfo,
}) => {
  const frenchEmployee = legalEntity && legalEntity.country === 'fr'
  const canSeeTaxResidence = shouldShowTaxResidence(employee, legalEntity)
  switch (category) {
    case DataCollectionCategories.address:
      return <EmployeeAddressBlock employee={employee} readonly={readonly} />
    case DataCollectionCategories.familySituation:
      return frenchEmployee ? (
        <FamilySituation employee={employee} readonly={readonly} legalEntity={legalEntity} />
      ) : (
        <Warning message={i18n.t('tasks.types.data_collection.details.forbidden_message_fr')} />
      )
    case DataCollectionCategories.fiscal_residence:
      return (
        <TaxResidence
          employee={employee}
          isSpanish={isSpanish}
          readonly={readonly}
          legalEntity={legalEntity}
          canSee={canSeeTaxResidence}
        />
      )
    case DataCollectionCategories.bankInformation:
      return (
        <BankInfo
          employee={employee}
          canEditPersonalInfo={canEditPersonalInfo}
          canSeeBankInfo={canSeePersonalInfo}
        />
      )
    case DataCollectionCategories.emergency_contact:
      return <ContactBlock employee={employee} readonly={readonly} />
    case DataCollectionCategories.personalSituation:
      return <PersonalSituation employee={employee} readonly={readonly} />
    case DataCollectionCategories.generalInformation:
      return (
        <GeneralBlock
          employee={employee}
          readonly={readonly}
          legalEntity={legalEntity}
          payroll={isEligibleForPayroll}
        />
      )
    case DataCollectionCategories.identificationNumber:
      return (
        <Identification
          employee={employee}
          readonly={readonly}
          legalEntity={legalEntity}
          payroll={isEligibleForPayroll}
        />
      )
    case DataCollectionCategories.employee_questions_cf:
      return (
        <EmployeeQuestionsCustomFields
          fieldIds={fieldIds}
          employee={employee}
          customFieldsOnly
          editableFieldsOnly
          contractVersions={contractVersions}
        />
      )
    default:
      return null
  }
}

export default EmployeeData
